import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography, Paper, Button, Chip, CircularProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AgricultureIcon from "@mui/icons-material/Agriculture";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import GardenBoundaryPreview from "../components/Layout/GardenBoundaryPreview";
import { useTranslate } from "../components/Translate/TranslateContext";
import { grabFarm } from "../api/farms";

export default function FarmView() {
    const navigate = useNavigate();
    const location = useLocation();
    const { translate } = useTranslate();

    const farm_id = location.state?.farm_id;

    const [farm, setFarm] = useState({});
    const [gardens, setGardens] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        const loadFarm = async () => {
            if (!farm_id) {
                navigate("/dashboard");
                return;
            }

            try {
                const data = await grabFarm(farm_id);

                if (data?.success) {
                    setFarm(Array.isArray(data.farm) ? data.farm[0] ?? {} : data.farm ?? {});
                    setGardens(Array.isArray(data.gardens?.[0]) ? data.gardens[0] : data.gardens ?? []);
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        loadFarm();
    }, [farm_id, navigate]);

    if (loading) {
        return (
            <Box sx={{ minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "var(--color-bg-primary)" }}>
                <CircularProgress />
            </Box>
        );
    }
const gardenCountLabel = gardens.length === 1 ? translate("garden") : translate("gardens");

    return (
        <Box sx={{ backgroundColor: "var(--color-bg-primary)", minHeight: "100vh", p: 4 }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/dashboard")}
                sx={{ mb: 3, color: "var(--color-text-primary)", textTransform: "none" }}
            >
                {translate("back")}
            </Button>

            <Paper
                elevation={0}
                sx={{
                    p: 4,
                    borderRadius: 4,
        border: "2px solid var(--color-green-strong)",
                    backgroundColor: "var(--color-green-soft)",
                    mb: 4,
                }}
            >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
                    <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                            <AgricultureIcon sx={{ color: "var(--color-green-main)" }} />

                            <Typography sx={{ fontFamily: "Fraunces", fontSize: 34, color: "var(--color-text-primary)" }}>
                                {farm[0]?.farm_name}
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <LocationOnOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />

                            <Typography color="text.secondary">
                                {farm[0]?.region || translate("unknown_region")}
                            </Typography>
                        </Box>
                    </Box>

                    <Chip
                        label={translate(farm[0]?.farm_status || "inactive")}
                        sx={{
                            borderRadius: 2,
                            backgroundColor: farm[0].farm_status === "active"
                                ? "rgba(36,88,42,0.15)"
                                : "rgba(0,0,0,0.08)",
                            color: "var(--color-text-primary)",
                            fontWeight: 600,
                        }}
                    />
                </Box>

                <Typography sx={{ mt: 3, color: "text.secondary" }}>
                    {gardens.length} {gardenCountLabel}
                </Typography>
            </Paper>


            <Box sx={{ display: "grid", gap: 2 }}>
                {gardens.map((garden) => (
    <Paper
    key={garden.garden_id}
    elevation={0}
    sx={{
        p: 1.25,
        borderRadius: 4,
        border: "2px solid var(--color-green-strong)",
        backgroundColor: "var(--color-bg-primary)",
    }}
>
    <Box
        sx={{
            display: "grid",
            gridTemplateColumns: {
                xs: "1fr",
                md: "1fr 1fr",
            },
            gap: 4,
            alignItems: "center",
        }}
    >
        <Box>
            <Typography
                sx={{
                    fontWeight: 700,
                    fontSize: 25,
                    color: "var(--color-text-primary)",
                    mb: 3,
                }}
            >
                {garden.garden_name}
            </Typography>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(120px, 1fr))",
                    gap: 3,
                }}
            >
                <Box>
                    <Typography variant="caption">
                        {translate("crop_type")}
                    </Typography>

                    <Typography>
                        {garden.crop_type || "-"}
                    </Typography>
                </Box>

                <Box>
                    <Typography variant="caption">
                        {translate("cultivar")}
                    </Typography>

                    <Typography>
                        {garden.cultivar_type || "-"}
                    </Typography>
                </Box>

                <Box>
                    <Typography variant="caption">
                        {translate("soil_type")}
                    </Typography>

                    <Typography>
                        {garden.soil_type
                            ? translate(garden.soil_type)
                            : "-"}
                    </Typography>
                </Box>

                <Box>
                    <Typography variant="caption">
                        {translate("area")}
                    </Typography>

                    <Typography>
                        {garden.garden_area_hectares || 0} ha
                    </Typography>
                </Box>

                <Box>
                    <Typography variant="caption">
                        {translate("latitude")}
                    </Typography>

                    <Typography>
                        {garden.latitude_center}
                    </Typography>
                </Box>
                <Box>
                    <Typography variant="caption">
                        {translate("longitude")}
                    </Typography>

                    <Typography>
                        {garden.longitude_center}
                    </Typography>
                </Box>
            </Box>
        </Box>

        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 220,
            }}
        >
            <GardenBoundaryPreview
                geometry={garden.geometry}
            />
        </Box>
    </Box>
</Paper>
                ))}
            </Box>
        </Box>
    );
}